/**
 * The open interest published for the most recently completed session.
 *
 * OPRA stamps open interest once, around 06:30 ET, and the figure reports positions as they
 * stood after the PREVIOUS session finished trading. That number is what settles a session's
 * opening-vs-closing question — and it is not in the EOD series, because an EOD row for a day
 * only exists after that day has closed.
 *
 * So on the morning after a session traded, the settling figure exists but nothing the
 * contract view already holds contains it: the EOD rows stop at the completed session and
 * carry the PREVIOUS one's figure, and `todaySnapshot` is a field no backend ever populates.
 * Without this call the last completed session reads "Pending" until the following close —
 * a full extra day of saying nothing about a question that has already been answered.
 *
 * One request per contract opened. The response carries its own publication timestamp, which
 * is what the reading is labelled with, so a stale or unexpected stamp is visible rather than
 * silently attributed to the wrong session.
 */

export interface PublishedOpenInterest {
  openInterest: number;
  /** When OPRA stamped it — the morning AFTER the session it reports. */
  publishedAt: string;
  /** Date part of `publishedAt`, the boundary a session must fall before to be settled. */
  publishedOn: string;
}

/** Theta wants the strike in thousandths of a dollar. */
function strikeParam(strike: number): string {
  return String(Math.round(strike * 1000));
}

function expirationParam(expiry: string): string {
  return String(expiry || '').replace(/[^0-9]/g, '').slice(0, 8);
}

export function buildPublishedOiUrl(
  base: string,
  p: { underlying: string; strike: number; expiry: string; optionType?: string },
): string | null {
  const exp = expirationParam(p.expiry);
  if (!p.underlying || !exp || !Number.isFinite(p.strike) || p.strike <= 0) return null;
  const right = String(p.optionType || '').toLowerCase().startsWith('p') ? 'P' : 'C';
  const u = new URL(base.startsWith('http') ? base : `${window.location.origin}${base}`);
  u.searchParams.set('type', 'open_interest');
  u.searchParams.set('symbol', p.underlying);
  u.searchParams.set('expiration', exp);
  u.searchParams.set('strike', strikeParam(p.strike));
  u.searchParams.set('right', right);
  return u.toString();
}

/** Reads the figure out of the Theta snapshot envelope, or null when it carries none. */
export function parsePublishedOi(json: unknown): PublishedOpenInterest | null {
  if (!json || typeof json !== 'object') return null;
  const resp = (json as { response?: unknown }).response;
  if (!Array.isArray(resp) || resp.length === 0) return null;
  const first = resp[0] as { data?: unknown };
  const data = Array.isArray(first?.data) ? first.data[0] : first?.data;
  if (!data || typeof data !== 'object') return null;
  const rec = data as { open_interest?: unknown; timestamp?: unknown };
  const oi = typeof rec.open_interest === 'number' ? rec.open_interest : Number(rec.open_interest);
  // Zero is not a published figure; it is the absence of one, and treating it as real would
  // report every contract as having been closed out overnight.
  if (!Number.isFinite(oi) || oi <= 0) return null;
  const publishedAt = String(rec.timestamp || '').trim();
  if (!publishedAt) return null;
  return { openInterest: oi, publishedAt, publishedOn: publishedAt.slice(0, 10) };
}
